<script lang="ts">
  import { Save } from 'lucide-svelte';
  import { setError, setLoading, uiState } from '$lib/state/ui.svelte';
  import { getErrorMessage } from '$lib/api';
  import { router } from '$lib/router';
  import { inputDevicesQuery } from '$lib/queries/input_devices';
  import { roomsQuery } from '$lib/queries/rooms';
  import { createInputDeviceMutation } from '$lib/mutations/input_devices';
  import {
    getNextAvailableAddress,
    getAvailableAddresses,
    DEVICE_TYPE_TO_GROUP_ID,
    DEVICE_TYPE_LABELS
  } from '$lib/constants/device_types';
  import type { InputDeviceResponse, DeviceType } from '$lib/types/input_device';

  let {
    onsuccess = (_device: InputDeviceResponse) => {},
    oncancel = () => {}
  }: {
    onsuccess?: (device: InputDeviceResponse) => void;
    oncancel?: () => void;
  } = $props();

  type DeviceFormData = Record<string, never>;

  const devices = inputDevicesQuery();
  const rooms = roomsQuery();
  const createDevice = createInputDeviceMutation();

  let unitId = $derived(router.unitId ?? 0);
  let dialogData = $derived(uiState.createDialogData as DeviceFormData | null);
  let selectedRoomId = $state<number>(0);

  $effect(() => {
    if (dialogData && 'roomId' in dialogData && typeof (dialogData as any).roomId === 'number') {
      selectedRoomId = (dialogData as any).roomId;
    } else if (dialogData) {
      selectedRoomId = 0;
    }
  });

const KEYPAD_KEY_OPTIONS = [1, 2, 3, 4, 6];
  const LOGICBOARD_KEY_OPTIONS = [1, 2, 3, 4];
  const CAT6_BRANCHES = [1, 3, 4, 5, 6, 7, 8];

  let selectedType = $state<DeviceType>('standard_keypad');
  let selectedKeyNum = $state<number>(1);
  let selectedAddress = $state<number>(0);
  let selectedCat6Branch = $state<number>(1);

  let unitDevices = $derived((devices.data ?? []).filter((d) => d.unit === unitId));

  let showKeyNum = $derived(selectedType === 'standard_keypad' || selectedType === 'logic_board');
  let showAddress = $derived(selectedType === 'standard_keypad' || selectedType === 'logic_board');
  let keyNumOptions = $derived(
    selectedType === 'standard_keypad' ? KEYPAD_KEY_OPTIONS : LOGICBOARD_KEY_OPTIONS
  );

  let nextAddress = $derived(
    showAddress ? getNextAvailableAddress(unitDevices, selectedType) : null
  );

  let availableAddresses = $derived(
    showAddress ? getAvailableAddresses(unitDevices, selectedType) : []
  );

  let addressGroupId = $derived(
    showAddress ? DEVICE_TYPE_TO_GROUP_ID[selectedType] : null
  );

  // Manual address entry: pre-filled with the next available address, but the
  // user can type any address. addressEdited tracks whether they have, so we
  // stop clobbering their input while still re-defaulting on type switches.
  let addressEdited = $state(false);

  $effect(() => {
    selectedType;
    addressEdited = false;
  });

  $effect(() => {
    if (showAddress && !addressEdited && nextAddress != null) {
      selectedAddress = nextAddress;
    }
  });

  let addressError = $derived.by(() => {
    if (!showAddress) return '';
    if (!Number.isInteger(selectedAddress) || selectedAddress < 1 || selectedAddress > 64) {
      return 'Address must be between 1 and 64.';
    }
    if (!availableAddresses.includes(selectedAddress)) {
      return availableAddresses.length === 0
        ? `No addresses available in group ${addressGroupId} for this unit.`
        : `Address ${selectedAddress} is already in use or reserved in group ${addressGroupId}.`;
    }
    return '';
  });

  let canCreate = $derived(!showAddress || addressError === '');

  function autoGenerateName(type: DeviceType, unitId: number): string {
    const existing = (devices.data ?? []).filter((d) => d.type === type && d.unit === unitId);
    const base = DEVICE_TYPE_LABELS[type];
    const count = existing.length + 1;
    let name = `${base} ${count}`;
    let attempts = 0;
    while (existing.some((d) => d.name === name) && attempts < 100) {
      attempts++;
      name = `${base} ${count + attempts}`;
    }
    return name;
  }

  async function saveDevice() {
    if (!selectedRoomId || !unitId) return;

    if (showAddress && addressError) {
      setError(addressError);
      return;
    }

    try {
      setLoading(true);
      const result = await createDevice.mutateAsync({
        type: selectedType,
        name: autoGenerateName(selectedType, unitId),
        location_room: selectedRoomId,
        cat6_branch: selectedCat6Branch,
        ...(showKeyNum && { key_num: selectedKeyNum }),
        ...(showAddress && { address: selectedAddress })
      });
      uiState.createDialogData = null;
      onsuccess(result);
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }
</script>

<form class="flex flex-col space-y-4">
  <div class="grid grid-cols-2 gap-6">
    <div class="flex flex-col">
      <label for="room" class="text-sm font-medium text-text-secondary">Room</label>
      <select
        name="room"
        id="room"
        bind:value={selectedRoomId}
        class="mt-1 px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary"
      >
        {#each rooms.data ?? [] as room}
          <option value={room.id}>{room.name}</option>
        {/each}
      </select>
    </div>
    <div class="flex flex-col">
      <label class="text-sm font-medium text-text-secondary mb-2">Device Type</label>
      <div class="grid grid-cols-2 gap-2">
        {#each Object.entries(DEVICE_TYPE_LABELS) as [type, label]}
          <button
            type="button"
            class="px-3 py-2 text-sm rounded-lg border transition-colors duration-100 text-left
              {selectedType === type
              ? 'border-accent-primary bg-accent-primary/10 text-accent-primary'
              : 'border-border-default bg-bg-rail text-text-secondary hover:border-border-strong'}"
            onclick={() => {
              selectedType = type as DeviceType;
            }}
          >
            {label}
          </button>
        {/each}
      </div>
    </div>
  </div>

  <div class="grid grid-cols-2 gap-6">
    <div class="flex flex-col">
      <label for="cat6_branch" class="text-sm font-medium text-text-secondary">Cat6 Branch</label>
      <select
        name="cat6_branch"
        id="cat6_branch"
        bind:value={selectedCat6Branch}
        class="mt-1 px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary"
      >
        {#each CAT6_BRANCHES as branch}
          <option value={branch}>Branch {branch}</option>
        {/each}
      </select>
    </div>

    {#if showKeyNum}
      <div class="flex flex-col">
        <label for="key_num" class="text-sm font-medium text-text-secondary">
          {selectedType === 'standard_keypad' ? 'Keypad Size' : 'Key Count'}
        </label>
        <select
          name="key_num"
          id="key_num"
          bind:value={selectedKeyNum}
          class="mt-1 px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary"
        >
          {#each keyNumOptions as option}
            <option value={option}>{option} Key{option !== 1 ? 's' : ''}</option>
          {/each}
        </select>
      </div>
    {/if}
  </div>

  {#if showAddress}
    <div class="flex flex-col">
      <label for="address" class="text-sm font-medium text-text-secondary">
        Address <span class="text-text-secondary font-normal">(group {addressGroupId})</span>
      </label>
      <input
        type="number"
        name="address"
        id="address"
        min="1"
        max="64"
        bind:value={selectedAddress}
        oninput={() => (addressEdited = true)}
        class="mt-1 px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary
          {addressError ? 'border-red-500' : ''}"
      />
      {#if addressError}
        <p class="mt-1 text-sm text-red-600">{addressError}</p>
      {:else if nextAddress != null}
        <p class="mt-1 text-xs text-text-secondary">Next available: {nextAddress}</p>
      {/if}
    </div>
  {/if}

  <div class="flex justify-end">
    <button
      type="button"
      onclick={saveDevice}
      disabled={!canCreate}
      class="mt-4 inline-flex items-center gap-2 px-6 py-2 bg-accent-primary text-white rounded-lg shadow-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent-primary disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Save class="w-5 h-5" />
      Create Device
    </button>
  </div>
</form>
